import { Fragment, useEffect } from "react";
import { Disclosure, Menu, Transition } from "@headlessui/react";
import { Bars3Icon, XMarkIcon } from "@heroicons/react/24/outline";
import { Link, useLocation } from "react-router-dom";

// * Won files
import {
  BrightToggler,
  NightToggle,
} from "../../features/DarkModeTogglerSlice";
import logo from "../../Images/logo.png";
import GifMoon from "../../videos/moon.gif";
import GifSun from "../../videos/sun.gif";
import { useDispatch, useSelector } from "react-redux";
import {
  CreateUniqId,
  formatTimeFromISOString,
  matchGenderToTitle,
  truncateString,
} from "../../Utility";
import { setUSerSingOut } from "../../features/UserLoginStatusSlice";
import { getDeviceInfo } from "../LoggedInComponents/Helper";

function classNames(...classes) {
  return classes.filter(Boolean).join(" ");
}

function matchAnyOne(currentPath, givenPaths) {
  return givenPaths.some((path) => currentPath === path);
}

export default function Navbar() {
  const dispatch = useDispatch();

  // * Start ***Enable Dark Mode Feature***
  const { navbarBackgroundColor, textLightColor } =
    useSelector((state) => state.setMode).css || {};

  const navbarBackgroundColorDynamic = navbarBackgroundColor;
  const textLightColorDynamic = textLightColor;

  // * Dark Mode enable button function
  const darkModeState = useSelector((state) => state.setMode).darkMode;

  // * Get the redux state
  const ifUserLogIn = useSelector((state) => state.user).login;

  // * Get user info from redux store [All (data fetch & set in redux) in profile.jsx]
  const userName =
    useSelector((state) => state.user)?.profile_Info?.name || "NAN";
  const userGender =
    useSelector((state) => state.user)?.profile_Info?.gender || "NAN";
  const userLastLogIn =
    useSelector((state) => state.user)?.profile_Info?.time_Records
      ?.last_Login || "NAN";
  useEffect(() => {
    getDeviceInfo();
  }, [userLastLogIn]);
  // * Path location
  const location = useLocation();
  const currentPath = location.pathname;

  // * Array for Quick Access nav list if user loggedIn = True
  const loggedIn_True_QuickAccess = [
    {
      name: "ManageYour Account",
      href: "/account/profile/overview",
    },
    {
      name: "Your Profile",
      href: "/account/profile/info",
    },
    {
      name: "Settings",
      href: "/account/profile/settings",
    },
    {
      name: "Sign out",
      href: "",
    },
  ];

  // * Array for Quick Access nav list if user loggedIn= False
  const loggedIn_False_QuickAccess = [
    { name: "Sing up", href: "/signup" },
    {
      name: "Log In",
      href: "/login",
    },
  ];

  // * Array for nav list if user loggedIn = True
  const loggedIn_False = [
    {
      name: "About",
      href: "/about",
      current: matchAnyOne(currentPath, ["/about"]),
    },
    {
      name: "Usage guide",
      href: "/user-guide",
      current: matchAnyOne(currentPath, ["/user-guide"]),
    },
    {
      name: "Log In",
      href: "/login",
      current: matchAnyOne(currentPath, ["/login"]),
    },
    {
      name: "Sing up",
      href: "/signup",
      current: matchAnyOne(currentPath, ["/signup"]),
    },
  ];

  // * Array for nav list if user loggedIn = False
  const loggedIn_True = [
    {
      name: "Account",
      href: "/account",
      current: matchAnyOne(currentPath, ["/account"]),
    },

    {
      name: "Dashboard",
      href: "/account/dashboard",
      current: matchAnyOne(currentPath, ["/account/dashboard"]),
    },

    {
      name: "Usage guide",
      href: "/user-guide",
      current: matchAnyOne(currentPath, ["/user-guide"]),
    },

    {
      name: "About",
      href: "/about",
      current: matchAnyOne(currentPath, ["/about"]),
    },

    {
      name: "Profile Management",
      href: "/account/profile",
      current: matchAnyOne(currentPath, [
        "/account/profile",
        "/account/profile/info",
        "/account/profile/overview",
        "/account/profile/settings",
      ]),
    },
    {
      name: "Sign out",
      href: "",
      current: currentPath === "/signout" ? true : false,
    },
  ];

  // * Give functions to nav Items
  function processValue(input) {
    let result;
    const defaultFunction = () => {};
    switch (input) {
      case "Sign out":
        result = setUSerSingOut();
        break;

      default:
        result = defaultFunction;
        break;
    }

    return result;
  }

  // * Array for navigation in quick Access button
  const quickAccess = ifUserLogIn
    ? loggedIn_True_QuickAccess
    : loggedIn_False_QuickAccess;

  // * Array for navigation bar
  const navigation = ifUserLogIn ? loggedIn_True : loggedIn_False;

  // * Dark Mode Enable Function
  function enableDisableDarkMode(params) {
    if (darkModeState) {
      return dispatch(BrightToggler());
    }
    dispatch(NightToggle());
  }

  return (
    <Disclosure
      as="nav"
      className={` ${navbarBackgroundColorDynamic}  fixed top-0 left-0 right-0 z-[100] py-1 border-b-2 border-gray-200 `}
    >
      {({ open }) => (
        <>
          <div className="mx-auto max-w-7xl px-2 sm:px-6 lg:px-8">
            <div className="relative flex h-16 items-center justify-between">
              <div className="absolute inset-y-0 left-0 flex items-center sm:hidden">
                {/* Mobile menu button*/}
                <Disclosure.Button className="inline-flex items-center  justify-center rounded-md p-2 text-gray-400 hover:bg-gray-700 hover:text-white focus:outline-none focus:ring-2 focus:ring-inset focus:ring-white">
                  <span className="sr-only">Open main menu</span>
                  {open ? (
                    <XMarkIcon className="block h-6 w-6" aria-hidden="true" />
                  ) : (
                    <Bars3Icon className="block h-6 w-6" aria-hidden="true" />
                  )}
                </Disclosure.Button>
              </div>
              <div className="flex flex-1 items-center justify-center sm:items-stretch sm:justify-start">
                <div className="flex flex-wrap flex-col flex-shrink-0 items-center">
                  <Link to="/">
                    <h1 className=" text-[#ffa200] font-bold text-lg cursor-pointer">
                      jsonOnClouds
                    </h1>
                  </Link>
                  {ifUserLogIn ? (
                    <>
                      <h1
                        className={` ${textLightColorDynamic} hidden md:block font-bold text-sm text-center italic truncate`}
                      >
                        {truncateString(
                          `Welcome  ${matchGenderToTitle(
                            userGender
                          )} ${userName}`,
                          36
                        )}
                      </h1>
                      <p
                        className={` font-mono text-xs ${textLightColorDynamic}`}
                      >
                        Last Log-In{" "}
                        {userLastLogIn !== "NAN" ? (
                          formatTimeFromISOString(userLastLogIn)
                        ) : (
                          <i className="ri-loader-2-fill"></i>
                        )}
                      </p>
                    </>
                  ) : (
                    <></>
                  )}
                </div>
                <div className="hidden sm:ml-6 sm:block my-auto">
                  <div className="flex justify-center items-center space-x-4">
                    {navigation.map((item) => (
                      <Link
                        onClick={() => dispatch(processValue(item.name))}
                        key={CreateUniqId()}
                        to={item.href}
                        className={classNames(
                          item.current
                            ? "bg-gray-900 text-white"
                            : `${textLightColorDynamic} hover:bg-gray-700 hover:text-white `,
                          "rounded-md px-3 py-2 text-sm font-extrabold"
                        )}
                        aria-current={item.current ? "page" : undefined}
                      >
                        {item.name}
                      </Link>
                    ))}
                  </div>
                </div>
              </div>
              <div className="absolute inset-y-0 right-0 flex justify-center items-center pr-2 sm:static sm:inset-auto sm:ml-6 sm:pr-0">
                <button
                  onClick={() => enableDisableDarkMode()}
                  type="button"
                  className={`rounded-full ${navbarBackgroundColorDynamic} p-1 text-gray-400 hover:text-white inline-flex justify-center items-center`}
                >
                  {darkModeState === true}
                  <span className="sr-only">
                    {darkModeState ? "Dark Mode Enable" : "Light Mode Enable"}
                  </span>
                  <div className="inline-flex rounded-md bg-slate-50  ring-1 ring-white cursor-pointer hover:bg-yellow-50 focus:bg-yellow-50 transition duration-200 delay-150 hover:delay-200  focus:delay-150  shadow-inner">
                    <img
                      src={darkModeState ? GifMoon : GifSun}
                      className=" w-8 h-8 sm:w-9 sm:h-9"
                      alt="Dark mode now enable"
                      loading="eager"
                    />
                  </div>
                </button>

                {/* Profile dropdown */}
                <Menu as="div" className="relative ml-3">
                  <div className={``}>
                    <Menu.Button
                      className={`flex rounded-full  ${navbarBackgroundColorDynamic} text-sm focus:outline-none focus:ring-2 focus:ring-[#ffa20071] focus:ring-offset-2 focus:ring-offset-gray-800`}
                    >
                      <span className="sr-only">Open user menu</span>
                      <img
                        className=" h-9 w-auto  rounded-full"
                        src={logo}
                        alt="Your Company"
                        loading="eager"
                      />
                    </Menu.Button>
                  </div>
                  <Transition
                    as={Fragment}
                    enter="transition ease-out duration-100"
                    enterFrom="transform opacity-0 scale-95"
                    enterTo="transform opacity-100 scale-100"
                    leave="transition ease-in duration-75"
                    leaveFrom="transform opacity-100 scale-100"
                    leaveTo="transform opacity-0 scale-95"
                  >
                    <Menu.Items className="absolute right-0 z-10 mt-2 w-48 origin-top-right rounded-md bg-white py-1 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                      {quickAccess.map((element, Index) => (
                        <Menu.Item key={CreateUniqId()}>
                          {({ active }) => (
                            <Link
                              onClick={() =>
                                dispatch(processValue(element.name))
                              }
                              to={element.href}
                              className={classNames(
                                active ? "bg-gray-100" : "",
                                "block px-4 py-2 text-sm text-gray-700"
                              )}
                            >
                              {element.name}
                            </Link>
                          )}
                        </Menu.Item>
                      ))}
                    </Menu.Items>
                  </Transition>
                </Menu>
              </div>
            </div>
          </div>

          <Disclosure.Panel className="sm:hidden">
            <div className="space-y-1 px-2 pb-3 pt-2">
              {navigation.map((item) => (
                <Link
                  onClick={() => dispatch(processValue(item.name))}
                  to={item.href}
                  key={CreateUniqId()}
                >
                  <Disclosure.Button
                    as="a"
                    className={
                      "" +
                      classNames(
                        item.current
                          ? "bg-gray-900 text-white"
                          : ` ${textLightColorDynamic} hover:bg-gray-700 hover:text-white`,
                        "block rounded-md px-3 py-2 text-base font-medium"
                      )
                    }
                    aria-current={item.current ? "page" : undefined}
                  >
                    {item.name}
                  </Disclosure.Button>
                </Link>
              ))}
              {ifUserLogIn ? (
                <h1
                  className={` ${textLightColorDynamic} font-bold text-sm text-center italic truncate`}
                >
                  {truncateString(
                    `Welcome  ${matchGenderToTitle(userGender)} ${userName}`,
                    36
                  )}
                </h1>
              ) : (
                <></>
              )}
            </div>
          </Disclosure.Panel>
        </>
      )}
    </Disclosure>
  );
}
